import { useEffect, useState, useMemo } from "react";
import toast from "react-hot-toast";
import {
    FiCalendar,
    FiPlus,
    FiClock,
    FiCheckCircle,
    FiXCircle,
    FiAlertTriangle,
    FiX,
    FiSend,
    FiRefreshCw,
} from "react-icons/fi";
import leaveService from "../../../services/leaveService";

const STATUS_CONFIG = {
    Pending: { bg: "#fef3c7", color: "#92400e", icon: FiClock, label: "Pending Approval" },
    Approved: { bg: "#dcfce7", color: "#15803d", icon: FiCheckCircle, label: "Approved" },
    Rejected: { bg: "#fee2e2", color: "#dc2626", icon: FiXCircle, label: "Rejected" },
};

const computeInclusiveDays = (from, to) => {
    if (!from || !to) return 0;
    const f = new Date(`${from}T00:00:00.000Z`);
    const t = new Date(`${to}T00:00:00.000Z`);
    const diffMs = t.getTime() - f.getTime();
    if (diffMs < 0) return 0;
    return Math.round(diffMs / 86400000) + 1;
};

export default function TeacherLeave() {
    const [balanceData, setBalanceData] = useState(null);
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [statusFilter, setStatusFilter] = useState("");
    const [showApplyModal, setShowApplyModal] = useState(false);

    // Form state
    const [form, setForm] = useState({
        leaveType: "",
        fromDate: "",
        toDate: "",
        reason: "",
    });
    const [submitting, setSubmitting] = useState(false);

    const loadData = async (isManualRefresh = false) => {
        if (isManualRefresh) setRefreshing(true);
        else setLoading(true);

        try {
            const [balRes, appsRes] = await Promise.all([
                leaveService.getMyLeaveBalance(),
                leaveService.getMyLeaveApplications(),
            ]);

            setBalanceData(balRes.data?.data ?? balRes.data);
            setApplications(appsRes.data?.data ?? appsRes.data ?? []);
        } catch (err) {
            toast.error(err.response?.data?.message || err.message || "Failed to load leave records");
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const balances = useMemo(() => balanceData?.balance || [], [balanceData]);
    const academicYearLabel = balanceData?.academicYear || "";

    const selectedTypeBalance = useMemo(() => {
        if (!form.leaveType) return null;
        return balances.find((b) => String(b.leaveType._id) === String(form.leaveType));
    }, [form.leaveType, balances]);

    const totalDaysRequested = useMemo(() => {
        return computeInclusiveDays(form.fromDate, form.toDate);
    }, [form.fromDate, form.toDate]);

    const isOverBalance = Boolean(
        selectedTypeBalance &&
        totalDaysRequested > 0 &&
        totalDaysRequested > selectedTypeBalance.available
    );

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleOpenModal = (preselectedTypeId = "") => {
        setForm({
            leaveType: preselectedTypeId || (balances[0]?.leaveType?._id || ""),
            fromDate: "",
            toDate: "",
            reason: "",
        });
        setShowApplyModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.leaveType) return toast.error("Please select a leave type");
        if (!form.fromDate || !form.toDate) return toast.error("Please enter valid start and end dates");
        if (form.toDate < form.fromDate) return toast.error("End date cannot be earlier than start date");
        if (!form.reason.trim()) return toast.error("Please provide a reason for leave");

        setSubmitting(true);
        try {
            const res = await leaveService.submitMyLeaveApplication({
                leaveType: form.leaveType,
                fromDate: form.fromDate,
                toDate: form.toDate,
                reason: form.reason.trim(),
            });

            if (res.data?.warning) {
                toast(res.data.warning, { icon: "⚠️", duration: 5000 });
            }
            toast.success("Leave application submitted successfully");
            setShowApplyModal(false);
            loadData(true);
        } catch (err) {
            toast.error(err.response?.data?.message || err.message || "Failed to submit leave application");
        } finally {
            setSubmitting(false);
        }
    };

    const filteredApplications = useMemo(() => {
        if (!statusFilter) return applications;
        return applications.filter((app) => app.status === statusFilter);
    }, [applications, statusFilter]);

    if (loading) {
        return (
            <div className="loading-center">
                <div className="spinner" /> Loading leave details…
            </div>
        );
    }

    return (
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
            {/* Page Header */}
            <div className="page-header" style={{ flexWrap: "wrap", gap: "0.75rem", marginBottom: "1.25rem" }}>
                <div>
                    <h1 className="page-title" style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <FiCalendar size={22} color="#7c3aed" /> My Leave Management
                    </h1>
                    <p className="page-subtitle">
                        View leave balances, submit requests, and track approval status · {academicYearLabel}
                    </p>
                </div>
                <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                    <button
                        className="btn btn-outline"
                        onClick={() => loadData(true)}
                        disabled={refreshing}
                        title="Refresh leave balance and records"
                    >
                        <FiRefreshCw size={13} className={refreshing ? "spin-icon" : ""} /> Refresh
                    </button>
                    <button className="btn btn-primary" onClick={() => handleOpenModal()}>
                        <FiPlus size={14} /> Apply for Leave
                    </button>
                </div>
            </div>

            {/* Leave Balances Cards */}
            <div style={{ marginBottom: "1.5rem" }}>
                <div style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-muted)", marginBottom: "0.75rem" }}>
                    Annual Leave Quotas & Balances ({academicYearLabel})
                </div>

                {balances.length === 0 ? (
                    <div className="card" style={{ padding: "1.5rem", textAlign: "center", color: "var(--text-muted)" }}>
                        No leave types configured for your role. Contact college administration.
                    </div>
                ) : (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "1rem" }}>
                        {balances.map((b) => {
                            const pctUsed = b.allowedPerYear > 0 ? Math.min(100, Math.round((b.used / b.allowedPerYear) * 100)) : 0;
                            const isLow = b.available <= 2 && b.allowedPerYear > 4;
                            const accentColor = b.available === 0 ? "#dc2626" : isLow ? "#d97706" : "#7c3aed";

                            return (
                                <div
                                    key={b.leaveType._id}
                                    className="card"
                                    style={{
                                        padding: "1.25rem",
                                        borderLeft: `4px solid ${accentColor}`,
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "space-between",
                                        transition: "transform 0.15s, box-shadow 0.15s",
                                    }}
                                >
                                    <div>
                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                                            <div>
                                                <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>{b.leaveType.name}</h3>
                                                {b.leaveType.code && (
                                                    <span style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 600 }}>
                                                        Code: {b.leaveType.code}
                                                    </span>
                                                )}
                                            </div>
                                            <span style={{
                                                fontSize: 11.5,
                                                fontWeight: 700,
                                                padding: "2px 8px",
                                                borderRadius: 99,
                                                background: b.available > 0 ? "#ede9fe" : "#fee2e2",
                                                color: b.available > 0 ? "#7c3aed" : "#dc2626",
                                            }}>
                                                {b.available} day{b.available !== 1 ? "s" : ""} left
                                            </span>
                                        </div>

                                        {/* Progress Bar */}
                                        <div style={{ height: 6, background: "var(--border, #e2e8f0)", borderRadius: 3, overflow: "hidden", margin: "10px 0 12px" }}>
                                            <div style={{ width: `${pctUsed}%`, height: "100%", background: accentColor, borderRadius: 3 }} />
                                        </div>

                                        {/* Breakdown Details */}
                                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 4, textAlign: "center", fontSize: 12, borderTop: "1px solid var(--border)", paddingTop: 8 }}>
                                            <div>
                                                <div style={{ color: "var(--text-muted)", fontSize: 11 }}>Quota</div>
                                                <div style={{ fontWeight: 700 }}>{b.allowedPerYear}d</div>
                                            </div>
                                            <div>
                                                <div style={{ color: "var(--text-muted)", fontSize: 11 }}>Used</div>
                                                <div style={{ fontWeight: 700, color: "#15803d" }}>{b.used}d</div>
                                            </div>
                                            <div>
                                                <div style={{ color: "var(--text-muted)", fontSize: 11 }}>Pending</div>
                                                <div style={{ fontWeight: 700, color: b.pending > 0 ? "#d97706" : "var(--text-muted)" }}>
                                                    {b.pending}d
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <button
                                        type="button"
                                        className="btn btn-outline btn-sm"
                                        style={{ marginTop: 12, width: "100%", justifyContent: "center" }}
                                        onClick={() => handleOpenModal(b.leaveType._id)}
                                    >
                                        Apply for {b.leaveType.code || b.leaveType.name}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Leave Applications History */}
            <div className="card">
                <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.75rem" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <span style={{ fontWeight: 700 }}>Application History</span>
                        <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                            ({filteredApplications.length} record{filteredApplications.length !== 1 ? "s" : ""})
                        </span>
                    </div>

                    <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                        <label style={{ fontSize: 12.5, color: "var(--text-muted)" }}>Filter status:</label>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="form-control"
                            style={{ fontSize: 12.5, minWidth: 120, padding: "4px 8px" }}
                        >
                            <option value="">All Applications</option>
                            <option value="Pending">Pending</option>
                            <option value="Approved">Approved</option>
                            <option value="Rejected">Rejected</option>
                        </select>
                    </div>
                </div>

                {filteredApplications.length === 0 ? (
                    <div className="card-body" style={{ textAlign: "center", color: "var(--text-muted)", padding: "2.5rem 1rem" }}>
                        {statusFilter ? `No ${statusFilter.toLowerCase()} applications found.` : "You have not submitted any leave applications yet."}
                    </div>
                ) : (
                    <div className="table-wrapper">
                        <table>
                            <thead>
                                <tr>
                                    <th>Leave Type</th>
                                    <th>Duration</th>
                                    <th>Days</th>
                                    <th>Reason</th>
                                    <th>Applied On</th>
                                    <th style={{ textAlign: "center" }}>Status</th>
                                    <th>Review Remarks</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredApplications.map((app) => {
                                    const sc = STATUS_CONFIG[app.status] || STATUS_CONFIG.Pending;
                                    const StatusIcon = sc.icon;
                                    return (
                                        <tr key={app._id}>
                                            <td style={{ fontWeight: 600 }}>
                                                {app.leaveType?.name || "Leave"}
                                                {app.leaveType?.code && (
                                                    <span style={{ fontSize: 11, color: "var(--text-muted)", marginLeft: 4 }}>
                                                        ({app.leaveType.code})
                                                    </span>
                                                )}
                                            </td>
                                            <td style={{ whiteSpace: "nowrap", fontSize: 13 }}>
                                                {app.fromDate} <span style={{ color: "var(--text-muted)" }}>&rarr;</span> {app.toDate}
                                            </td>
                                            <td style={{ fontWeight: 700, textAlign: "center" }}>
                                                {app.totalDays}
                                            </td>
                                            <td style={{ fontSize: 12.5, maxWidth: 220 }}>
                                                <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={app.reason}>
                                                    {app.reason}
                                                </div>
                                            </td>
                                            <td style={{ fontSize: 12, color: "var(--text-muted)", whiteSpace: "nowrap" }}>
                                                {new Date(app.createdAt).toLocaleDateString("en-IN", {
                                                    day: "2-digit",
                                                    month: "short",
                                                    year: "numeric",
                                                })}
                                            </td>
                                            <td style={{ textAlign: "center" }}>
                                                <span style={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    gap: 4,
                                                    padding: "2px 8px",
                                                    borderRadius: 99,
                                                    fontSize: 11.5,
                                                    fontWeight: 600,
                                                    background: sc.bg,
                                                    color: sc.color,
                                                }}>
                                                    <StatusIcon size={11} /> {sc.label}
                                                </span>
                                            </td>
                                            <td style={{ fontSize: 12, color: app.reviewRemarks ? "var(--text)" : "var(--text-muted)", fontStyle: app.reviewRemarks ? "italic" : "normal" }}>
                                                {app.reviewRemarks || (app.status === "Pending" ? "Awaiting review" : "—")}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Apply Leave Modal */}
            {showApplyModal && (
                <div className="modal-overlay" onClick={() => !submitting && setShowApplyModal(false)}>
                    <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 540 }}>
                        <div className="modal-header">
                            <div>
                                <h2 style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: 16 }}>
                                    <FiCalendar color="#7c3aed" /> Apply for Leave
                                </h2>
                                <div className="modal-subtitle">
                                    Submit your leave request for administrative review ({academicYearLabel})
                                </div>
                            </div>
                            <button
                                type="button"
                                className="btn btn-icon"
                                onClick={() => !submitting && setShowApplyModal(false)}
                                style={{ color: "var(--text-muted)", borderRadius: 8, padding: 6 }}
                                title="Close"
                            >
                                <FiX size={18} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="modal-body" style={{ display: "flex", flexDirection: "column", gap: "1.1rem" }}>
                                {/* Leave Type Selection */}
                                <div className="form-group" style={{ margin: 0 }}>
                                    <label className="form-label" style={{ fontWeight: 600, fontSize: 13, marginBottom: 6, display: "block" }}>
                                        Leave Type <span style={{ color: "var(--danger, #dc2626)" }}>*</span>
                                    </label>
                                    <select
                                        name="leaveType"
                                        value={form.leaveType}
                                        onChange={handleFormChange}
                                        className="form-control"
                                        required
                                        style={{ fontWeight: 500 }}
                                    >
                                        <option value="" disabled>Select Leave Type</option>
                                        {balances.map((b) => (
                                            <option key={b.leaveType._id} value={b.leaveType._id}>
                                                {b.leaveType.name} ({b.leaveType.code || "LEAVE"}) — {b.available} day{b.available !== 1 ? "s" : ""} available
                                            </option>
                                        ))}
                                    </select>

                                    {/* Quota overview badge */}
                                    {selectedTypeBalance && (
                                        <div
                                            style={{
                                                marginTop: 8,
                                                padding: "0.5rem 0.75rem",
                                                background: "#f8fafc",
                                                border: "1px solid var(--border, #e2e8f0)",
                                                borderRadius: 8,
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                fontSize: 12,
                                            }}
                                        >
                                            <span style={{ color: "var(--text-muted)" }}>
                                                Quota: <strong>{selectedTypeBalance.allowedPerYear}d</strong>
                                            </span>
                                            <span style={{ color: "var(--text-muted)" }}>
                                                Used: <strong style={{ color: "#15803d" }}>{selectedTypeBalance.used}d</strong>
                                            </span>
                                            <span style={{ color: "var(--text-muted)" }}>
                                                Pending: <strong style={{ color: selectedTypeBalance.pending > 0 ? "#d97706" : "inherit" }}>{selectedTypeBalance.pending}d</strong>
                                            </span>
                                            <span
                                                style={{
                                                    background: selectedTypeBalance.available > 0 ? "#ede9fe" : "#fee2e2",
                                                    color: selectedTypeBalance.available > 0 ? "#7c3aed" : "#dc2626",
                                                    fontWeight: 700,
                                                    padding: "2px 8px",
                                                    borderRadius: 99,
                                                    fontSize: 11.5,
                                                }}
                                            >
                                                {selectedTypeBalance.available} days left
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* Date Range Grid */}
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.85rem" }}>
                                    <div className="form-group" style={{ margin: 0 }}>
                                        <label className="form-label" style={{ fontWeight: 600, fontSize: 13, marginBottom: 6, display: "block" }}>
                                            From Date <span style={{ color: "var(--danger, #dc2626)" }}>*</span>
                                        </label>
                                        <input
                                            type="date"
                                            name="fromDate"
                                            value={form.fromDate}
                                            onChange={handleFormChange}
                                            className="form-control"
                                            required
                                        />
                                    </div>
                                    <div className="form-group" style={{ margin: 0 }}>
                                        <label className="form-label" style={{ fontWeight: 600, fontSize: 13, marginBottom: 6, display: "block" }}>
                                            To Date <span style={{ color: "var(--danger, #dc2626)" }}>*</span>
                                        </label>
                                        <input
                                            type="date"
                                            name="toDate"
                                            value={form.toDate}
                                            min={form.fromDate || undefined}
                                            onChange={handleFormChange}
                                            className="form-control"
                                            required
                                        />
                                    </div>
                                </div>

                                {/* Duration & Quota Status Notification */}
                                {totalDaysRequested > 0 && (
                                    <div
                                        style={{
                                            padding: "0.65rem 0.85rem",
                                            borderRadius: 8,
                                            fontSize: 12.5,
                                            display: "flex",
                                            alignItems: "flex-start",
                                            gap: 10,
                                            background: isOverBalance ? "#fff7ed" : "#f0fdf4",
                                            border: isOverBalance ? "1px solid #fed7aa" : "1px solid #bbf7d0",
                                            color: isOverBalance ? "#9a3412" : "#166534",
                                            lineHeight: 1.45,
                                        }}
                                    >
                                        {isOverBalance ? (
                                            <>
                                                <FiAlertTriangle color="#ea580c" size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                                                <div>
                                                    <strong>Notice:</strong> Requested duration is <strong>{totalDaysRequested} day(s)</strong>, which exceeds your remaining quota ({selectedTypeBalance?.available || 0} days). The application will be forwarded to the administrator for discretionary review.
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <FiCheckCircle color="#16a34a" size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                                                <div>
                                                    Total Duration: <strong>{totalDaysRequested} day(s)</strong>. Sufficient quota is available for this request.
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}

                                {/* Reason for Leave */}
                                <div className="form-group" style={{ margin: 0 }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                                        <label className="form-label" style={{ fontWeight: 600, fontSize: 13, margin: 0 }}>
                                            Reason for Leave <span style={{ color: "var(--danger, #dc2626)" }}>*</span>
                                        </label>
                                        <small style={{ color: "var(--text-muted)", fontSize: 11.5 }}>
                                            {form.reason.length} / 500
                                        </small>
                                    </div>
                                    <textarea
                                        name="reason"
                                        value={form.reason}
                                        onChange={handleFormChange}
                                        rows={3}
                                        className="form-control"
                                        placeholder="Please provide a clear and concise reason for your leave application..."
                                        required
                                        maxLength={500}
                                        style={{ resize: "vertical" }}
                                    />
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-outline"
                                    disabled={submitting}
                                    onClick={() => setShowApplyModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={submitting}
                                    style={{ display: "inline-flex", alignItems: "center", gap: 6, minWidth: 140, justifyContent: "center" }}
                                >
                                    {submitting ? (
                                        <>
                                            <span className="spinner" style={{ width: 14, height: 14 }} /> Submitting…
                                        </>
                                    ) : (
                                        <>
                                            <FiSend size={13} /> Submit Application
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
